'use strict';

import React from 'react';
import Relay from 'react-relay/classic';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import LazyLoad from 'react-lazy-load';
import qs from 'qs';
import get from 'lodash.get';

import verticalHelpers from 'dibs-vertical-helpers';
import RelatedFolderItem from '../Favorites/RelatedFolderItem';
import RelatedFavoriteItem from '../Favorites/RelatedFavoriteItem';
import { trackProductClick, trackProductImpressions } from '../../actions/pdpTrackingActions.es';
import { isMobile, isTablet } from 'dibs-media-query';

import QuickViewButton from '../../../components/ProductQuickView/QuickViewButton';
import QuickView from '../../../components/ProductQuickView/QuickView';

import CarouselItem from './../Carousel/CarouselItem';
import RelatedItemPrice from '../RelatedItemPrice/RelatedItemPrice';
import RelatedItemDiscount from '../RelatedItemPrice/RelatedItemDiscount';

class GridItem extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            itemLoaded: false,
            loadLazyImages: props.loadLazyImages,
        };

        this._handleClick = this._handleClick.bind(this);
        this._showInfo = this._showInfo.bind(this);
        this._hideInfo = this._hideInfo.bind(this);
        this._imageVisible = this._imageVisible.bind(this);
        this._handleImpression = this._handleImpression.bind(this);
    }

    componentWillReceiveProps(props) {
        this.setState({ loadLazyImages: props.loadLazyImages });
    }

    _handleClick() {
        const { gridType } = this.props;
        if (this.context.executeAction) {
            this.context.executeAction(trackProductClick, {
                product: this.props,
                listName: `pdp ${gridType}`,
            });
        }
        if (this.props.onProductClick) {
            this.props.onProductClick(this.props); //using this.props as the full product since fields were destructured
        }
    }

    _handleImpression() {
        const { gridType } = this.props;
        if (this.context.executeAction) {
            this.context.executeAction(trackProductImpressions, {
                product: this.props,
                listName: `pdp ${gridType}`,
            });
        }
    }

    _showInfo() {
        this.props.setQuickViewId(this.props.id);
    }

    _hideInfo() {
        this.props.setQuickViewId(null);
    }

    _renderQuickView(isQuickViewOpen) {
        const {
            attributes,
            creators,
            description,
            location,
            materials,
            seller,
            title,
        } = this.props;
        let sellerName;
        if (typeof seller === 'string') {
            sellerName = seller;
        } else if (seller && seller.company) {
            sellerName = seller.company;
        }
        return (
            <QuickView
                isActive={isQuickViewOpen}
                onClose={this._hideInfo}
                title={title}
                location={location}
                creators={creators}
                seller={sellerName}
                attributes={attributes}
                materials={materials}
                description={description}
            />
        );
    }

    _setImageSize() {
        // LazyLoad height when it has no content
        let size = 240;

        if (isMobile()) {
            size = 133;
        } else if (isTablet()) {
            size = 189;
        }

        return size;
    }

    _imageVisible() {
        this._handleImpression();
        this.setState({ itemLoaded: true });

        if (!this.state.loadLazyImages) {
            this.props.cancelLazyLoad();
        }
    }

    _squareImage(image) {
        const size = this._setImageSize();
        /*
         * if the `small` image path in inventory is not populated, the PHP server will send the value down to the
         * carousel as null.  `GridItem` will dereference the `null` value and break.
         */
        const parts = image ? image.split('?') : [];
        const queryParams = qs.parse(parts[1]);
        Object.assign(queryParams, {
            width: size,
            height: size,
            canvas: `${size},${size}`,
            fit: 'bounds',
        });

        const queryParamsString = qs.stringify(queryParams).replace('%2C', ','); // do not encode comma
        return `${parts[0]}?${queryParamsString}`;
    }

    _renderTitle({ title, itemTitleTag: ItemTitleTag, vertical, creator, creationDate }) {
        if (vertical === verticalHelpers.VERTICAL_ART) {
            return (
                <ItemTitleTag className="pdp-grid-item-text">
                    <span className="pdp-grid-item-creator">{creator}</span>
                    {title}
                    {creationDate && `, ${creationDate}`}
                </ItemTitleTag>
            );
        }
        return <ItemTitleTag className="pdp-grid-item-text">{title}</ItemTitleTag>;
    }

    render() {
        const {
            userId,
            userIds,
            itemId,
            item,
            viewer,
            uri,
            title,
            index,
            image,
            selectedItemIds,
            gridType,
            addFavoriting,
            allowQuickView,
            itemTitleTag,
            loadPortfolioData,
            shouldFillInFolder,
            onFavorited,
            onAddItemSuccess,
            vertical,
            creator,
            creation_date: creationDate,
            priceUI,
            currency,
        } = this.props;
        const { itemLoaded } = this.state;
        const isQuickViewOpen = this.props.id === this.props.quickViewItemId;
        const offsetVertical = this.state.loadLazyImages
            ? document.body.offsetHeight / 2
            : this._setImageSize() * 3;

        const itemLinkClassName = classNames('pdp-grid-item-link', {
            'is-blur': isQuickViewOpen,
        });
        const imageWrapperClass = classNames('pdp-grid-item-image-wrapper', {
            'is-loading': !itemLoaded,
        });

        const relatedItemProps = {
            userId,
            itemId,
            viewer,
            selectedItemIds,
            gridType,
            title,
            image,
        };
        const pdpFavoriteItemRelayVariables = {
            loadPortfolioData,
            userId,
            userIds,
            selectedItemIds,
        };

        return (
            <div className="pdp-grid-item-content">
                <a href={uri} className={itemLinkClassName} onClick={this._handleClick}>
                    <div className={imageWrapperClass}>
                        <div className="pdp-grid-lazy-wrapper">
                            <LazyLoad
                                offsetVertical={offsetVertical}
                                onContentVisible={this._imageVisible}
                            >
                                <CarouselItem
                                    className="pdp-grid-item-image"
                                    title={title}
                                    src={this._squareImage(image)}
                                    index={index}
                                />
                            </LazyLoad>
                        </div>
                    </div>
                    {addFavoriting && (
                        <div>
                            <RelatedFavoriteItem
                                onFavorited={onFavorited}
                                {...relatedItemProps}
                                {...pdpFavoriteItemRelayVariables}
                            />
                            <RelatedFolderItem
                                item={item}
                                shouldFillInFolder={shouldFillInFolder}
                                onAddItemSuccess={onAddItemSuccess}
                                {...relatedItemProps}
                                {...pdpFavoriteItemRelayVariables}
                            />
                        </div>
                    )}
                </a>

                <div className="pdp-grid-item-text-wrap">
                    {allowQuickView && (
                        <div className="pdp-grid-quick-view-button">
                            <QuickViewButton onClick={this._showInfo} />
                        </div>
                    )}
                    <a href={uri} className={itemLinkClassName} onClick={this._handleClick}>
                        {this._renderTitle({
                            title,
                            itemTitleTag,
                            vertical,
                            creator,
                            creationDate,
                        })}
                        <div className="pdp-grid-item-price">
                            <RelatedItemPrice priceUI={priceUI} currency={currency} />
                            {get(priceUI, 'discount_percentage') && (
                                <span className="pdp-grid-item-price-separator">
                                    <RelatedItemDiscount priceUI={priceUI} />
                                </span>
                            )}
                        </div>
                    </a>
                </div>

                {allowQuickView && this._renderQuickView(isQuickViewOpen)}
            </div>
        );
    }
}

GridItem.propTypes = {
    id: PropTypes.string.isRequired,
    itemId: PropTypes.string,
    userId: PropTypes.string,
    item: PropTypes.object,
    viewer: PropTypes.object,
    uri: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onProductClick: PropTypes.func,
    setQuickViewId: PropTypes.func,
    quickViewItemId: PropTypes.string,
    gridType: PropTypes.string,
    location: PropTypes.string,
    vertical: PropTypes.string,
    creator: PropTypes.string,
    creators: PropTypes.string,
    creation_date: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // eslint-disable-line
    attributes: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    seller: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    materials: PropTypes.string,
    description: PropTypes.string,
    userIds: PropTypes.array,
    selectedItemIds: PropTypes.array,
    addFavoriting: PropTypes.bool,
    itemTitleTag: PropTypes.string,
    allowQuickView: PropTypes.bool,
    shouldFillInFolder: PropTypes.bool,
    loadPortfolioData: PropTypes.bool,
    onFavorited: PropTypes.func,
    onAddItemSuccess: PropTypes.func,
    cancelLazyLoad: PropTypes.func,
    loadLazyImages: PropTypes.bool,
    priceUI: PropTypes.object,
    currency: PropTypes.string,
};

GridItem.defaultProps = {
    gridType: 'more from dealer',
    selectedItemIds: [],
    addFavoriting: true,
    itemTitleTag: 'span',
    allowQuickView: true,
    cancelLazyLoad: () => {},
};

GridItem.contextTypes = {
    getStore: PropTypes.func,
    executeAction: PropTypes.func,
};

module.exports = {
    GridItem,
    GridItemRelayContainer: Relay.createContainer(GridItem, {
        initialVariables: {
            userId: null,
            userIds: [],
            selectedItemIds: [],
            shouldFillInFolder: false,
            loadPortfolioData: false,
        },
        fragments: {
            viewer: params => Relay.QL`
                    fragment on Viewer {
                        ${RelatedFolderItem.getFragment('viewer', params)}
                        ${RelatedFavoriteItem.getFragment('viewer', params)}
                    }
                `,
            item: params => Relay.QL`
                    fragment on Item {
                        ${RelatedFolderItem.getFragment('item', params)}
                    }
                `,
        },
    }),
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Grid/GridItem.jsx